// biome-ignore lint/style/useImportType: <explanation>
import React, { useState } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import {
  Button,
  Typography,
  Paper,
  Box,
  CircularProgress,
  TextField
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(3),
    margin: theme.spacing(2),
  },
  inputContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  textField: {
    flexGrow: 1,
  },
  button: {
    minWidth: '120px',
  },
  result: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
  error: {
    color: theme.palette.error.main,
  },
  success: {
    color: theme.palette.success.main,
  },
  response: {
    whiteSpace: 'pre-wrap',
    fontSize: '14px',
    lineHeight: 1.6,
  },
  searchInfo: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.info.light,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.info.contrastText,
    fontSize: '12px',
  },
  responseContainer: {
    position: 'relative',
  },
}));

export const SourcebotChatComponent = () => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchPerformed, setSearchPerformed] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<number>(0);
  const [useStreaming, setUseStreaming] = useState<boolean>(true);

  const sendMessage = async () => {
    if (!message.trim()) return;

    setLoading(true);
    setError(null);
    setResponse(null);
    setSearchPerformed(false);
    setSearchResults(0);

    try {
      console.log('[Sourcebot Chat] Sending message:', message);

      if (useStreaming) {
        // Use streaming endpoint
        await handleStreamingResponse();
      } else {
        // Use regular endpoint
        await handleRegularResponse();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('[Sourcebot Chat] Error:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRegularResponse = async () => {
    const { token } = await identityApi.getCredentials();
    const apiResponse = await fetch('/api/sourcebot-backend/sourcebot-chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ message }),
    });

    if (!apiResponse.ok) {
      throw new Error(`HTTP ${apiResponse.status}: ${apiResponse.statusText}`);
    }

    const data = await apiResponse.json();
    console.log('[Sourcebot Chat] Success:', data);

    setResponse(data.response || 'No response received');
    setSearchPerformed(data.searchPerformed || false);
    setSearchResults(data.searchResults || 0);
  };

  const handleStreamingResponse = async () => {
    const { token } = await identityApi.getCredentials();
    const apiResponse = await fetch('/api/sourcebot-backend/sourcebot-chat/stream', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ message }),
    });

    if (!apiResponse.ok) {
      throw new Error(`HTTP ${apiResponse.status}: ${apiResponse.statusText}`);
    }

    // Handle streaming response
    const reader = apiResponse.body?.getReader();
    if (!reader) {
      throw new Error('Failed to get response stream reader');
    }

    const decoder = new TextDecoder();
    let accumulatedResponse = '';

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        accumulatedResponse += chunk;

        // Update the response progressively
        setResponse(accumulatedResponse);
      }

      // Mark as completed
      setSearchPerformed(true); // Assume search was performed for streaming
      setSearchResults(1); // Mock result count for streaming

    } finally {
      reader.releaseLock();
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  return (
    <Paper className={classes.paper}>
      <Typography variant="h5" gutterBottom>
        Sourcebot AI Assistant
      </Typography>

      <Typography variant="body2" color="textSecondary" gutterBottom>
        Ask questions about code, technologies, or development topics. The AI can search through your codebase to provide intelligent answers.
      </Typography>

      <Box style={{ marginBottom: '8px' }}>
        <label>
          <input
            type="checkbox"
            checked={useStreaming}
            onChange={(e) => setUseStreaming(e.target.checked)}
            style={{ marginRight: '8px' }}
          />
          Enable streaming responses (better UX)
        </label>
      </Box>

      <Box className={classes.inputContainer}>
        <TextField
          className={classes.textField}
          label="Ask a question..."
          placeholder="How does authentication work in this codebase?"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          multiline
          maxRows={4}
          variant="outlined"
          disabled={loading}
        />
        <Button
          className={classes.button}
          variant="contained"
          color="primary"
          onClick={sendMessage}
          disabled={loading || !message.trim()}
        >
          {loading ? <CircularProgress size={20} /> : 'Send'}
        </Button>
      </Box>

      {error && (
        <Box className={classes.result}>
          <Typography variant="h6" className={classes.error}>
            Error:
          </Typography>
          <Typography variant="body2" className={classes.error}>
            {error}
          </Typography>
        </Box>
      )}

      {response && (
        <Box className={`${classes.result} ${classes.responseContainer}`}>
          {searchPerformed && (
            <Box className={classes.searchInfo}>
              🔍 Code search performed • Found {searchResults} result{searchResults !== 1 ? 's' : ''}
            </Box>
          )}
          <Typography variant="h6" className={classes.success}>
            {searchPerformed ? 'AI Analysis (with code search):' : 'AI Response:'}
          </Typography>
          <Typography variant="body2" className={classes.response}>
            {response}
          </Typography>
        </Box>
      )}

      {loading && (
        <Box className={classes.result}>
          <Typography variant="body2">
            {useStreaming
              ? '🤖 AI is streaming its analysis...'
              : '🤖 AI is analyzing your question and searching the codebase...'
            }
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
